<template>
  <AModal
    :model-value="modelValue"
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="bpk__title">
        <i class="material-icons-round bpk__title-icon">library_add</i>
        진열할 도서 선택
      </span>
    </template>
    <template #sub>
      교재명 · 출판사 · 바코드(ISBN)로 찾아 여러 권을 한 번에 담을 수 있습니다.
    </template>

    <div class="bpk">
      <!-- 검색 -->
      <div class="bpk__filters">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">menu_book</i>
            교재명
          </label>
          <input
            v-model.trim="filters.title"
            class="p-input"
            type="text"
            placeholder="교재명 일부"
            @keyup.enter="search"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">business</i>
            출판사
          </label>
          <input
            v-model.trim="filters.publisher"
            class="p-input"
            type="text"
            placeholder="출판사 일부"
            @keyup.enter="search"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">qr_code_2</i>
            바코드 (ISBN)
          </label>
          <input
            v-model.trim="filters.barcode"
            class="p-input bpk__mono"
            type="text"
            placeholder="9791234567890"
            @keyup.enter="search"
          />
        </div>
        <div class="bpk__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">검색</ABtn>
          <ABtn kind="ghost" icon="refresh" @click="resetFilters">초기화</ABtn>
        </div>
      </div>

      <div v-if="loading" class="bpk__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>교재를 찾는 중...</p>
      </div>

      <AEmpty
        v-else-if="!books.length"
        size="sm"
        title="검색 결과가 없습니다"
        sub="검색어를 줄이거나 '초기화' 후 다시 찾아보세요."
      />

      <template v-else>
        <ATable :columns="columns" :rows="books" row-key="barcode" striped bordered>
          <template #cell:_select="{ row }">
            <label :class="['p-check', { 'is-disabled': isAlready(row) }]">
              <input
                type="checkbox"
                :checked="isPicked(row)"
                :disabled="isAlready(row)"
                @change="togglePick(row)"
              />
            </label>
          </template>

          <template #cell:book="{ row }">
            <div class="bpk__book">
              <img
                class="bpk__cover"
                :src="row.cover_image_url || COVER_FALLBACK"
                :alt="row.title"
                @error="onCoverError"
              />
              <div class="bpk__book-text">
                <span class="bpk__book-title">{{ row.title }}</span>
                <span class="bpk__book-meta">
                  {{ row.publisher || '출판사 미등록' }}
                  <template v-if="row.editor_name"> · {{ row.editor_name }}</template>
                </span>
              </div>
            </div>
          </template>

          <template #cell:classify="{ row }">
            <span class="bpk__classify">
              <template v-if="row.series_name">
                {{ row.series_name }}<template v-if="row.series_volume"> {{ row.series_volume }}권</template>
              </template>
              <template v-else>—</template>
              <template v-if="row.grade_label"> · {{ row.grade_label }}</template>
            </span>
          </template>

          <template #cell:_state="{ row }">
            <APill v-if="isAlready(row)" kind="default">진열됨</APill>
            <APill v-else-if="row.status !== 'approval'" kind="warn" dot>
              {{ textbookStatusLabel(row.status) }}
            </APill>
            <APill v-else kind="ok">앱 공개</APill>
          </template>
        </ATable>

        <APagination
          :current-page="page"
          :total="total"
          :page-size="size"
          @change="onPage"
        />
      </template>
    </div>

    <template #foot>
      <div class="bpk__tray">
        <template v-if="picked.length">
          <span class="bpk__tray-count">{{ picked.length }}권 선택</span>
          <div class="bpk__chips">
            <button
              v-for="b in picked"
              :key="b.barcode"
              type="button"
              class="bpk__chip"
              :title="`${b.title} 선택 해제`"
              @click="togglePick(b)"
            >
              {{ b.title }}
              <i class="material-icons-round">close</i>
            </button>
          </div>
        </template>
        <span v-else class="bpk__tray-empty">담을 도서를 체크하세요.</span>
      </div>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">취소</ABtn>
      <ABtn kind="primary" icon="add" :disabled="!picked.length || saving" :loading="saving" @click="add">
        {{ picked.length ? `${picked.length}권 추가` : '추가' }}
      </ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { COVER_FALLBACK, onCoverError, textbookStatusLabel } from "@/utils/homeSection";

const blankFilters = () => ({ title: "", publisher: "", barcode: "" });

// 추가 실패 사유. 서버가 부분 성공을 허용하고 skipped 로 이유를 돌려줍니다.
const SKIP_REASONS = {
  duplicate: "이미 진열됨",
  not_found: "교재를 찾을 수 없음",
  unpublished: "앱 공개 상태가 아님",
};

export default {
  name: "HomeSectionBookPickerModal",
  components: { AModal, ABtn, APill, ATable, AEmpty, APagination },
  props: {
    modelValue: { type: Boolean, default: false },
    sectionIdx: { type: Number, required: true },
    // 이미 이 섹션에 담긴 바코드. 중복 선택을 미리 막습니다.
    existingBarcodes: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "added"],
  data() {
    return {
      COVER_FALLBACK,
      loading: false,
      saving: false,
      filters: blankFilters(),
      books: [],
      total: 0,
      page: 1,
      size: 10,
      picked: [],
      columns: [
        { key: "_select", label: "", width: 44, align: "center" },
        { key: "book", label: "도서", width: 340 },
        { key: "barcode", label: "바코드 (ISBN)", width: 150, mono: true },
        { key: "classify", label: "시리즈 · 단계", width: 200, wrap: true },
        { key: "_state", label: "상태", width: 110, align: "center" },
      ],
    };
  },
  watch: {
    modelValue(open) {
      if (open) this.init();
    },
  },
  methods: {
    onCoverError,
    textbookStatusLabel,
    init() {
      this.saving = false;
      this.filters = blankFilters();
      this.picked = [];
      this.page = 1;
      this.fetchBooks();
    },
    isAlready(row) {
      return this.existingBarcodes.includes(row.barcode);
    },
    isPicked(row) {
      return this.picked.some((b) => b.barcode === row.barcode);
    },
    togglePick(row) {
      if (this.isAlready(row)) return;
      if (this.isPicked(row)) {
        this.picked = this.picked.filter((b) => b.barcode !== row.barcode);
      } else {
        // 페이지를 넘겨도 선택이 남아야 하므로 행 전체를 들고 있습니다.
        this.picked = [...this.picked, row];
      }
    },
    // 검색 조건이 바뀌면 항상 1페이지부터 다시 봅니다.
    search() {
      this.page = 1;
      this.fetchBooks();
    },
    resetFilters() {
      this.filters = blankFilters();
      this.page = 1;
      this.fetchBooks();
    },
    onPage(p) {
      this.page = p;
      this.fetchBooks();
    },
    async fetchBooks() {
      this.loading = true;
      try {
        // 공개 검색(/textbook/textbooks)이 아니라 관리자 목록을 씁니다.
        // total_count 가 오고 제목·출판사·바코드를 따로 걸 수 있기 때문입니다.
        const searchParams = {};
        Object.keys(this.filters).forEach((k) => {
          if (this.filters[k]) searchParams[k] = this.filters[k];
        });

        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/textbooks",
          params: {
            limit: this.size,
            page: this.page,
            search_params: JSON.stringify(searchParams),
            sort_field: "created_at",
            sort_order: "desc",
          },
          loadingBar: true,
        });
        this.books = res.data?.items || [];
        this.total = res.data?.total_count || 0;
      } catch (e) {
        console.error("교재 검색 실패:", e);
        this.books = [];
        this.total = 0;
        swal.fire("오류", apiErrorMessage(e, "교재를 검색하지 못했습니다."), "error");
      } finally {
        this.loading = false;
      }
    },
    /**
     * 선택한 도서를 섹션에 담습니다.
     * 저장까지 이 모달이 책임지고, 부모는 @added 에서 재조회만 합니다.
     */
    async add() {
      if (!this.picked.length) return;
      this.saving = true;
      try {
        const res = await this.$axios({
          method: "post",
          url: `/emp/home/sections/${this.sectionIdx}/items`,
          data: { barcodes: this.picked.map((b) => b.barcode) },
          loadingBar: true,
        });

        const added = res.data?.added_count ?? this.picked.length;
        const skipped = res.data?.skipped || [];

        if (skipped.length) {
          // 부분 성공. 무엇이 왜 빠졌는지 알려주지 않으면 "담았는데 없다"가 됩니다.
          // swal 의 text 는 개행을 살리지 않으므로 한 줄로 이어 붙입니다.
          const detail = skipped
            .map((s) => `${s.barcode} (${SKIP_REASONS[s.reason] || s.reason})`)
            .join(", ");
          await swal.fire({
            icon: added ? "warning" : "error",
            title: added ? `${added}권만 담았습니다` : "담지 못했습니다",
            text: `제외된 ${skipped.length}권 — ${detail}`,
          });
        } else {
          this.$notifyVue({ type: "success", title: `${added}권을 진열에 담았습니다.` });
        }

        this.$emit("update:modelValue", false);
        this.$emit("added");
      } catch (e) {
        console.error("진열 도서 추가 실패:", e);
        swal.fire("추가 실패", apiErrorMessage(e, "도서를 담지 못했습니다."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.bpk {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__filters {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
    gap: 12px;
    align-items: end;
    padding: 14px 15px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;

    @media (max-width: 860px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__filter-actions {
    display: inline-flex;
    gap: 8px;
  }

  &__mono {
    font-family: $p-font-mono;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__book {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__cover {
    width: 32px;
    height: 45px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid $p-line;
    background: $p-panel-alt;
    flex-shrink: 0;
  }

  &__book-text {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__book-title {
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
    white-space: normal;
    line-height: 1.35;
  }

  &__book-meta {
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__classify {
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__tray {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-right: auto;
    min-width: 0;
    overflow: hidden;
  }

  &__tray-count {
    font-size: 11px;
    font-weight: 800;
    color: $p-ink;
    white-space: nowrap;
  }

  &__tray-empty {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__chips {
    display: flex;
    gap: 5px;
    overflow-x: auto;
    padding-bottom: 2px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 180px;
    padding: 4px 7px;
    border: 1px solid $p-line;
    border-radius: 999px;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, color $p-dur $p-ease;

    &:hover { border-color: $p-err; color: $p-err-ink; }

    > i { font-size: 13px !important; flex-shrink: 0; }
  }
}
</style>
